import { useEffect, useState, useContext } from 'react';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import { AppContext, EMPTY_ARRAY } from 'commons-ui';
import { defaults, COLUMNS_NAME_CONFISCATION_DETAILS, KEYS_NAME_CONFISCATION_DETAILS } from '../../constants';
import { FraudFilters, pageDetailsType } from '../../types';
import useService from './useService';

const useList = () => {
	const { showAlert } = useContext(AppContext);
	const { getConfiscationAndRefundRequestInBulk } = useService();
	const [confiscationAndRefundData, setConfiscationAndRefundData] = useState<{ rowCount: number; data: any[] }>({
		rowCount: 0,
		data: []
	});
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const [filters, setFilters] = useState<FraudFilters>();
	const [showLoader, setShowLoader] = useState<boolean>(false);
	const [nextPage, setNextPage] = useState<boolean>(true);
	const [pageInfo, setPageInfo] = useState<pageDetailsType>(defaults.pageDetails);
	const [validPageLimit, setValidPageLimit] = useState<number>(0);
	const [downloadData, setDownloadData] = useState<any[]>([]);

	const fetchConfiscationAndRefundData = async () => {
		try {
			setShowLoader(true);
			const confiscationAndRefundRequestInBulk = await getConfiscationAndRefundRequestInBulk({
				pageDetails: pageInfo,
				filters
			});

			if (nextPage) {
				setValidPageLimit(validPageLimit + 1);
			}
			if (_isNil(confiscationAndRefundRequestInBulk.nextPageId)) {
				setNextPage(false);
			}
			setConfiscationAndRefundData({
				rowCount: confiscationAndRefundRequestInBulk.rowCount,
				data: confiscationAndRefundRequestInBulk.data
			});
			setForceUpdate(true);
		} catch (error: any) {
			setConfiscationAndRefundData({ data: [], rowCount: 0 });
			setForceUpdate(true);
		} finally {
			setShowLoader(false);
		}
	};

	useEffect(() => {
		if (filters) {
			fetchConfiscationAndRefundData();
			setDownloadData([]);
		}
	}, [filters]);

	useEffect(() => {
		if (filters && (nextPage || pageInfo.pageNo <= validPageLimit)) {
			fetchConfiscationAndRefundData();
		} else if (filters && !nextPage && pageInfo.pageNo > validPageLimit) {
			setConfiscationAndRefundData({ data: [], rowCount: 1000 });
			setForceUpdate(true);
			showAlert('No more data avaible for applied filter!', { type: 'error' });
		}
	}, [pageInfo.pageNo]);

	const getConfiscationAndRefundData = (prop: any) => {
		const { pageDetails } = prop;
		setPageInfo({ pageNo: pageDetails.pageNo, pageSize: pageDetails.pageSize });
		setForceUpdate(false);
		return confiscationAndRefundData;
	};

	const handleDownloadData = async () => {
		let currPageNo = 0;
		const dataArr = [];

		if (_isEmpty(downloadData)) {
			while (!_isNil(currPageNo)) {
				// eslint-disable-next-line no-await-in-loop
				const confiscationAndRefundRequestInBulk = await getConfiscationAndRefundRequestInBulk({
					pageDetails: { pageSize: pageInfo.pageSize, pageNo: currPageNo + 1 },
					filters
				});
				dataArr.push(...confiscationAndRefundRequestInBulk?.data);
				currPageNo = confiscationAndRefundRequestInBulk.nextPageId;
			}

			setDownloadData(dataArr);
		}

		const csvString = [
			COLUMNS_NAME_CONFISCATION_DETAILS,
			...((_isEmpty(downloadData) ? dataArr : downloadData) || EMPTY_ARRAY).map((item: any) => {
				return KEYS_NAME_CONFISCATION_DETAILS.map((key: any) => {
					if (key === 'createdAt' || key === 'updatedAt') {
						return dayjs(new Date(Number(item[key]))).format('YYYY-MM-DD hh:mm A');
					}
					return item[key];
				});
			})
		]
			.map((e) => e.join(','))
			.join('\n');
		const csvContent = `data:text/csv;charset=utf-8, ${csvString}`;
		const encodedUri = encodeURI(csvContent);

		const link = document.createElement('a');
		link.setAttribute('href', encodedUri);
		link.setAttribute('download', 'confiscationDetails.csv');
		document.body.appendChild(link);
		link.click();
	};

	const handleFilterSubmit = (submittedFilters: { fromToDate?: Date[] }) => {
		const { fromToDate } = submittedFilters;

		if (fromToDate && fromToDate.length === 2) {
			setFilters({ startTime: fromToDate[0].getTime(), endTime: fromToDate[1].getTime() + 86400000 });
		}
	};

	return {
		getConfiscationAndRefundData,
		handleFilterSubmit,
		forceUpdate,
		showLoader,
		setShowLoader,
		fetchConfiscationAndRefundData,
		handleDownloadData
	};
};

export default useList;
